import { CSSProperties, FC, ReactNode } from 'react';
import clsx from 'clsx';
import { Icon } from '@namespace/icons2';
import { useGetThemeConfig } from '@namespace/themes';
import { propsMerge } from '../../utils/propsMerge';
import { AText } from '../AText';
import { Box } from '../Box';

import styles from './index.module.css';

type AlertType = 'info' | 'warning' | 'error' | 'success';

const iconNameByType: Record<AlertType, string> = {
  info: 'info',
  warning: 'info',
  error: 'error',
  success: 'success'
};

interface AlertNotificationProps {
  message?: ReactNode;
  type?: AlertType;
  classNames?: {
    wrapperClassName?: string;
    messageClassName?: string;
  };
  onClose?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isWithIcon?: boolean;
  dataRole?: string;
  styles?: CSSProperties;
  customCrossIconStyles?: CSSProperties;
  iconSize?: 'xs' | 's' | 'm' | 'l';
  crossIconSize?: 'xs' | 's' | 'm' | 'l';
  fontSize?: 'xs' | 's' | 'm' | 'l';
  iconColor?: string;
  closeIconColor?: string;
  breed?: 'B' | 'R' | 'RU' | 'BU';
}

export const AlertNotification: FC<AlertNotificationProps> = ({ ...props }) => {
  const alertConfig = useGetThemeConfig('AlertNotification');
  const {
    classNames = {},
    message = '',
    type = 'info' as AlertType,
    onClose,
    onMouseEnter = () => {},
    onMouseLeave = () => {},
    isWithIcon = true,
    dataRole = 'alert-notification',
    iconSize = 's',
    crossIconSize = 'xs',
    fontSize = 's',
    breed = 'R',
    iconColor = 'txt_color_4',
    closeIconColor = 'txt_color_4'
  } = propsMerge(props, alertConfig);

  return (
    <Box
      data-role={dataRole}
      justify="between"
      className={clsx([
        styles.wrapper,
        classNames.messageClassName,
        styles[type]
      ])}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box align="center">
        {isWithIcon && (
          <Icon
            className={clsx([styles.typeIcon, classNames.typeIcon])}
            color={iconColor}
            size={iconSize}
            name={`icons/general/alert/${iconNameByType[type as AlertType]}`}
          />
        )}
        <AText
          breed={breed}
          size={fontSize}
          color="txt_color_4"
          className={classNames?.textClassName || ''}
        >
          {message}
        </AText>
      </Box>
      {onClose && (
        <Icon
          onClick={onClose}
          size={crossIconSize}
          color={closeIconColor}
          name="icons/general/action/close"
          className={styles.closeIcon}
        />
      )}
    </Box>
  );
};
